import {
  buildProductConditionKey,
  isProductConditionKey,
  parseProductConditionKey,
} from "@/util/conditional-logic-helpers";
import { PRODUCT_CONDITION_SEPARATOR } from "@/util/constants";

/**
 * Product targeting for show/hide conditional logic.
 *
 * Everything here exists to turn merchant clicks into the *existing* persisted key
 * vocabulary — `payment`, `product::<id>::*`, `product::<id>::<priceId>`,
 * `bump::<id>::<priceId>`. The runtime (`ghl-form-element`) already understands all
 * four; see `util/forms_and_surveys_helper.ts` and `FormCore.vue` there. No function
 * in this module may invent a new key shape.
 */

/** The payment element's own key — targeting it shows/hides the entire section. */
export const PAYMENT_SECTION_KEY = "payment";

export interface ProductCatalogVariant {
  priceId: string;
  name: string;
  amount?: number;
  currency?: string;
}

export interface ProductCatalogEntry {
  productId: string;
  name: string;
  variants: ProductCatalogVariant[];
}

export interface BumpCatalogEntry {
  productId: string;
  priceId: string;
  name: string;
  amount?: number;
  currency?: string;
}

export interface ProductCatalog {
  products: ProductCatalogEntry[];
  bumps: BumpCatalogEntry[];
}

/** Addresses one variant or bump inside the selection state. Never persisted. */
export const variantSelectionId = (productId: string, priceId: string): string =>
  `${productId}${PRODUCT_CONDITION_SEPARATOR}${priceId}`;

export interface ProductSelectionState {
  /** productId -> selected priceIds. Fully-selected products collapse to `::*` on emit. */
  productVariants: Map<string, Set<string>>;
  /** `variantSelectionId(productId, priceId)` per selected bump. */
  bumps: Set<string>;
  /**
   * Keys the catalog cannot resolve — a deleted product, or one whose details have not
   * loaded yet. Preserved verbatim and re-emitted, so editing one part of a rule never
   * silently drops targets the merchant still has saved.
   */
  unknownKeys: Set<string>;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

const asNonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const asFiniteNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

/**
 * Normalize the store's loosely-typed product lists into the shape the picker needs.
 * This is the single boundary where `addedProductList` / `bumProductList` are
 * interpreted; everything downstream works on `ProductCatalog`.
 *
 * Only `selectedPriceIds` variants are listed — those are the variants the merchant
 * actually exposes on the form, which is what a buyer can pick at runtime.
 */
export const buildProductCatalog = (
  addedProductList: unknown,
  bumpProductList: unknown
): ProductCatalog => {
  const products: ProductCatalogEntry[] = [];
  const bumps: BumpCatalogEntry[] = [];

  for (const rawProduct of Array.isArray(addedProductList) ? addedProductList : []) {
    const product = asRecord(rawProduct);
    const productId = asNonEmptyString(product?.productId);
    if (!product || !productId) {
      continue;
    }

    const priceById = new Map<string, Record<string, unknown>>();
    for (const rawPrice of Array.isArray(product.prices) ? product.prices : []) {
      const price = asRecord(rawPrice);
      const priceId = asNonEmptyString(price?._id);
      if (price && priceId) {
        priceById.set(priceId, price);
      }
    }

    const exposedPriceIds = Array.isArray(product.selectedPriceIds)
      ? product.selectedPriceIds
      : [...priceById.keys()];

    const variants: ProductCatalogVariant[] = [];
    for (const rawPriceId of exposedPriceIds) {
      const priceId = asNonEmptyString(rawPriceId);
      const price = priceId ? priceById.get(priceId) : undefined;
      if (!priceId || !price) {
        continue;
      }
      variants.push({
        priceId,
        name: asNonEmptyString(price.name) ?? "",
        amount: asFiniteNumber(price.amount),
        currency: asNonEmptyString(price.currency),
      });
    }

    products.push({
      productId,
      name: asNonEmptyString(product.name) ?? "",
      variants,
    });
  }

  for (const rawBump of Array.isArray(bumpProductList) ? bumpProductList : []) {
    const bump = asRecord(rawBump);
    const productId = asNonEmptyString(bump?.productId);
    const priceId = asNonEmptyString(bump?.priceId);
    if (!bump || !productId || !priceId) {
      continue;
    }
    bumps.push({
      productId,
      priceId,
      name: asNonEmptyString(bump.name) ?? "",
      amount: asFiniteNumber(bump.amount),
      currency: asNonEmptyString(bump.currency),
    });
  }

  return { products, bumps };
};

export interface ShowHideValueParts {
  /** Plain element keys (email, phone, …) — everything that is not a product target. */
  fieldKeys: string[];
  /** `product::` / `bump::` keys. */
  productKeys: string[];
  /** True when the payment element itself is targeted ("Entire section"). */
  hasEntireSection: boolean;
}

/**
 * Partition a persisted `outcome.value` into its three parts. Accepts the string form
 * (singular Show/Hide) and the array form (Show/Hide Multiple) alike — the same
 * normalization the runtime does — so rules authored before this change read back.
 */
export const splitShowHideValue = (value: unknown): ShowHideValueParts => {
  const entries = Array.isArray(value) ? value : [value];
  const fieldKeys: string[] = [];
  const productKeys: string[] = [];
  let hasEntireSection = false;

  for (const entry of entries) {
    if (typeof entry !== "string" || entry.length === 0) {
      continue;
    }
    if (isProductConditionKey(entry)) {
      productKeys.push(entry);
    } else if (entry === PAYMENT_SECTION_KEY) {
      hasEntireSection = true;
    } else {
      fieldKeys.push(entry);
    }
  }

  return { fieldKeys, productKeys, hasEntireSection };
};

export const createEmptyProductSelection = (): ProductSelectionState => ({
  productVariants: new Map(),
  bumps: new Set(),
  unknownKeys: new Set(),
});

export const cloneProductSelection = (
  selection: ProductSelectionState
): ProductSelectionState => ({
  productVariants: new Map(
    [...selection.productVariants].map(([productId, priceIds]) => [
      productId,
      new Set(priceIds),
    ])
  ),
  bumps: new Set(selection.bumps),
  unknownKeys: new Set(selection.unknownKeys),
});

/**
 * Expand persisted product keys into per-variant selection state for the picker.
 * `product::<id>::*` selects every variant the catalog lists for that product, so a
 * whole-product rule stays a whole-product rule across an edit.
 */
export const keysToProductSelection = (
  keys: Iterable<string>,
  catalog: ProductCatalog
): ProductSelectionState => {
  const selection = createEmptyProductSelection();
  const productById = new Map(catalog.products.map((product) => [product.productId, product]));
  const knownBumpIds = new Set(
    catalog.bumps.map((bump) => variantSelectionId(bump.productId, bump.priceId))
  );

  for (const key of keys) {
    const ref = parseProductConditionKey(key);
    if (!ref) {
      continue;
    }

    if (ref.kind === "bumpProduct") {
      const bumpId = ref.priceId ? variantSelectionId(ref.productId, ref.priceId) : null;
      if (bumpId && knownBumpIds.has(bumpId)) {
        selection.bumps.add(bumpId);
      } else {
        selection.unknownKeys.add(key);
      }
      continue;
    }

    const product = productById.get(ref.productId);
    if (!product || product.variants.length === 0) {
      selection.unknownKeys.add(key);
      continue;
    }

    const selected = selection.productVariants.get(ref.productId) ?? new Set<string>();
    if (ref.priceId === null) {
      product.variants.forEach((variant) => selected.add(variant.priceId));
    } else if (product.variants.some((variant) => variant.priceId === ref.priceId)) {
      selected.add(ref.priceId);
    } else {
      selection.unknownKeys.add(key);
    }

    if (selected.size > 0) {
      selection.productVariants.set(ref.productId, selected);
    }
  }

  return selection;
};

/**
 * Emit the minimal key set for a selection. A product with every catalog variant
 * selected collapses to `product::<id>::*` — one key instead of N, and a variant added
 * to the product later is covered automatically. Partial selections emit explicit
 * variant keys. Unresolvable keys pass through unchanged.
 */
export const productSelectionToKeys = (
  selection: ProductSelectionState,
  catalog: ProductCatalog
): string[] => {
  const keys: string[] = [];

  for (const product of catalog.products) {
    const selected = selection.productVariants.get(product.productId);
    if (!selected || selected.size === 0) {
      continue;
    }

    const selectedVariants = product.variants.filter((variant) =>
      selected.has(variant.priceId)
    );
    if (selectedVariants.length === 0) {
      continue;
    }

    if (selectedVariants.length === product.variants.length) {
      keys.push(
        buildProductConditionKey({
          kind: "product",
          productId: product.productId,
          priceId: null,
        })
      );
      continue;
    }

    selectedVariants.forEach((variant) => {
      keys.push(
        buildProductConditionKey({
          kind: "product",
          productId: product.productId,
          priceId: variant.priceId,
        })
      );
    });
  }

  for (const bump of catalog.bumps) {
    if (!selection.bumps.has(variantSelectionId(bump.productId, bump.priceId))) {
      continue;
    }
    keys.push(
      buildProductConditionKey({
        kind: "bumpProduct",
        productId: bump.productId,
        priceId: bump.priceId,
      })
    );
  }

  return [...keys, ...selection.unknownKeys];
};

export type ProductCheckState = "checked" | "indeterminate" | "unchecked";

export const resolveProductCheckState = (
  product: ProductCatalogEntry,
  selection: ProductSelectionState
): ProductCheckState => {
  const selected = selection.productVariants.get(product.productId);
  if (!selected || product.variants.length === 0) {
    return "unchecked";
  }
  const selectedCount = product.variants.filter((variant) =>
    selected.has(variant.priceId)
  ).length;
  if (selectedCount === 0) {
    return "unchecked";
  }
  return selectedCount === product.variants.length ? "checked" : "indeterminate";
};

/** Products (and bumps) with at least one selection — the "N of M products" numerator. */
export const countSelectedProducts = (
  selection: ProductSelectionState,
  catalog: ProductCatalog
): number => {
  const selectedProducts = catalog.products.filter(
    (product) => resolveProductCheckState(product, selection) !== "unchecked"
  ).length;
  const selectedBumps = catalog.bumps.filter((bump) =>
    selection.bumps.has(variantSelectionId(bump.productId, bump.priceId))
  ).length;
  return selectedProducts + selectedBumps;
};

/** The "N of M products" denominator. */
export const countTotalProducts = (catalog: ProductCatalog): number =>
  catalog.products.length + catalog.bumps.length;

/**
 * Selection transitions. Each returns a new state — the picker never mutates in place,
 * so Vue reactivity and undo/history stay predictable.
 */

export const setProductSelected = (
  selection: ProductSelectionState,
  product: ProductCatalogEntry,
  selected: boolean
): ProductSelectionState => {
  const next = cloneProductSelection(selection);
  if (selected) {
    next.productVariants.set(
      product.productId,
      new Set(product.variants.map((variant) => variant.priceId))
    );
  } else {
    next.productVariants.delete(product.productId);
  }
  return next;
};

export const setVariantSelected = (
  selection: ProductSelectionState,
  product: ProductCatalogEntry,
  priceId: string,
  selected: boolean
): ProductSelectionState => {
  const next = cloneProductSelection(selection);
  const variants = next.productVariants.get(product.productId) ?? new Set<string>();
  if (selected) {
    variants.add(priceId);
  } else {
    variants.delete(priceId);
  }
  if (variants.size > 0) {
    next.productVariants.set(product.productId, variants);
  } else {
    next.productVariants.delete(product.productId);
  }
  return next;
};

export const setBumpSelected = (
  selection: ProductSelectionState,
  bump: BumpCatalogEntry,
  selected: boolean
): ProductSelectionState => {
  const next = cloneProductSelection(selection);
  const bumpId = variantSelectionId(bump.productId, bump.priceId);
  if (selected) {
    next.bumps.add(bumpId);
  } else {
    next.bumps.delete(bumpId);
  }
  return next;
};

/** "Select all" — every product, every variant, every bump in the catalog. */
export const selectAllProducts = (
  selection: ProductSelectionState,
  catalog: ProductCatalog
): ProductSelectionState => {
  const next = cloneProductSelection(selection);
  catalog.products.forEach((product) => {
    if (product.variants.length > 0) {
      next.productVariants.set(
        product.productId,
        new Set(product.variants.map((variant) => variant.priceId))
      );
    }
  });
  catalog.bumps.forEach((bump) => {
    next.bumps.add(variantSelectionId(bump.productId, bump.priceId));
  });
  return next;
};

/**
 * "Clear" — drops every resolvable selection. Unknown keys are cleared too: the merchant
 * explicitly asked for an empty selection, so keeping invisible targets would be wrong.
 */
export const clearProductSelection = (): ProductSelectionState => createEmptyProductSelection();

/**
 * Distinct products/bumps referenced by a set of keys, without consulting the catalog.
 * Used for rule summaries, where a target whose product has since been deleted should
 * still be counted rather than silently vanish from the count.
 */
export const countProductTargetsFromKeys = (keys: Iterable<string>): number => {
  const targets = new Set<string>();
  for (const key of keys) {
    const ref = parseProductConditionKey(key);
    if (ref) {
      targets.add(`${ref.kind}:${ref.productId}`);
    }
  }
  return targets.size;
};

/** True when every product, variant and bump in the catalog is selected. */
export const isEntireCatalogSelected = (
  selection: ProductSelectionState,
  catalog: ProductCatalog
): boolean => {
  if (countTotalProducts(catalog) === 0) {
    return false;
  }
  const everyProduct = catalog.products.every(
    (product) => resolveProductCheckState(product, selection) === "checked"
  );
  const everyBump = catalog.bumps.every((bump) =>
    selection.bumps.has(variantSelectionId(bump.productId, bump.priceId))
  );
  return everyProduct && everyBump;
};

/** Drops every key the catalog cannot resolve, keeping the rest of the selection. */
export const removeUnknownProductKeys = (
  selection: ProductSelectionState
): ProductSelectionState => {
  const next = cloneProductSelection(selection);
  next.unknownKeys.clear();
  return next;
};
